<template>
	<div class="emergency-address-panel">
		<v-card flat class="panel-container">
			<!-- Initial State - Show button to generate report -->
			<div v-if="!loading && !report && !error" class="initial-state">
				<v-icon size="48" color="red darken-1" class="mb-4">mdi-shield-alert-outline</v-icon>
				<h3 class="initial-title">
					{{ $t('emergency-address-compliance') }}
				</h3>
				<p class="initial-description">
					{{ $t('emergency-report-description') }}
				</p>
				<v-btn
					color="red darken-1"
					dark
					large
					rounded
					class="generate-btn"
					@click="fetchReport"
				>
					<v-icon left>mdi-file-document-outline</v-icon>
					{{ $t('generate-report') }}
				</v-btn>
			</div>

			<!-- Loading State -->
			<div v-else-if="loading" class="loading-container">
				<v-progress-circular indeterminate color="red darken-1" />
				<span class="loading-text">{{ $t('loading') }}...</span>
			</div>

			<!-- Error State -->
			<v-alert
				v-else-if="error"
				type="error"
				outlined
				dense
				class="ma-4"
			>
				{{ error }}
			</v-alert>

			<!-- Content -->
			<div v-else-if="report" class="content-container">
				<!-- Queue Status -->
				<div class="queue-status" :class="report.isEmergencyQueueEmpty ? 'queue-empty' : 'queue-processing'">
					<v-icon small :color="report.isEmergencyQueueEmpty ? 'green' : 'orange'">
						{{ report.isEmergencyQueueEmpty ? 'mdi-check-circle' : 'mdi-progress-clock' }}
					</v-icon>
					<span>{{ report.isEmergencyQueueEmpty ? $t('queue-empty') : $t('queue-processing') }}</span>
				</div>

				<!-- Global Summary -->
				<div class="summary-section">
					<div class="summary-stats">
						<div class="stat-item compliant">
							<span class="stat-value">{{ report.summary?.compliantPhoneNumbers || 0 }}</span>
							<span class="stat-label">{{ $t('compliant') }}</span>
						</div>
						<div class="stat-item non-compliant">
							<span class="stat-value">{{ report.summary?.nonCompliantPhoneNumbers || 0 }}</span>
							<span class="stat-label">{{ $t('non-compliant') }}</span>
						</div>
						<div class="stat-item total">
							<span class="stat-value">{{ report.summary?.totalPhoneNumbers || 0 }}</span>
							<span class="stat-label">{{ $t('total') }}</span>
						</div>
					</div>
				</div>

				<!-- Organizations List -->
				<div class="organizations-section">
					<div class="section-header">
						<span class="section-title">{{ $t('organizations') }}</span>
						<v-chip small class="org-count-chip">
							{{ compliantOrganizationsCount }} / {{ report.organizations?.length || 0 }}
						</v-chip>
					</div>

					<!-- Search Field -->
					<div class="search-container">
						<v-text-field
							v-model="searchText"
							:placeholder="$t('search') + '...'"
							prepend-inner-icon="mdi-magnify"
							clearable
							hide-details
							outlined
							dense
							class="search-field"
						/>
						<div v-if="searchText" class="results-count">
							{{ filteredOrganizations.length }} {{ filteredOrganizations.length === 1 ? $t('organization').toLowerCase() : $t('organizations').toLowerCase() }}
						</div>
					</div>

					<v-expansion-panels v-model="expandedPanels" multiple flat>
						<v-expansion-panel
							v-for="org in filteredOrganizations"
							:key="org.organizationId"
							class="org-panel"
							:class="{ 'org-inactive': !org.isActive }"
						>
							<v-expansion-panel-header class="org-header">
								<div class="org-header-content">
									<div class="org-info">
										<div class="org-name-row">
											<span class="org-name">{{ org.organizationName || org.organizationId }}</span>
											<v-chip
												v-if="!org.isActive"
												x-small
												color="grey"
												text-color="white"
												class="ml-2"
											>
												inactive
											</v-chip>
										</div>
										<div class="org-id-row">
											<span class="org-id">{{ org.organizationId }}</span>
											<v-btn
												icon
												x-small
												class="copy-btn"
												@click.stop="copyToClipboard(org.organizationId)"
											>
												<v-icon x-small>mdi-content-copy</v-icon>
											</v-btn>
										</div>
									</div>
									<div class="org-status">
										<v-chip
											x-small
											:color="isOrgCompliant(org) ? 'green' : 'red'"
											text-color="white"
											class="status-chip"
										>
											{{ isOrgCompliant(org) ? $t('compliant') : $t('non-compliant') }}
										</v-chip>
										<span class="phone-count">
											{{ org.totalCount }} {{ $t('phone-numbers') }}
										</span>
									</div>
								</div>
							</v-expansion-panel-header>

							<v-expansion-panel-content>
								<!-- SIP Domain Info -->
								<div v-if="org.sipDomain" class="sip-domain-card" :class="{ 'sip-domain-error': !org.sipDomain.emergencyCallingEnabled || !org.sipDomain.emergencyCallerSid }">
									<div class="sip-domain-header">
										<v-icon small :color="isSipDomainCompliant(org.sipDomain) ? 'primary' : 'red'">mdi-web</v-icon>
										<span class="domain-name">{{ org.sipDomain.friendlyName }}</span>
										<div class="sip-status-chips">
											<v-chip
												x-small
												:color="org.sipDomain.emergencyCallingEnabled ? 'green' : 'red'"
												text-color="white"
											>
												{{ org.sipDomain.emergencyCallingEnabled ? $t('e911-enabled') : $t('e911-disabled') }}
											</v-chip>
											<v-chip
												x-small
												:color="org.sipDomain.emergencyCallerSid ? 'green' : 'red'"
												text-color="white"
											>
												{{ org.sipDomain.emergencyCallerSid ? $t('caller-id-configured') : $t('no-caller-id') }}
											</v-chip>
										</div>
									</div>
									<!-- SIP Domain Errors -->
									<div v-if="!org.sipDomain.emergencyCallingEnabled || !org.sipDomain.emergencyCallerSid" class="sip-errors">
										<div v-if="!org.sipDomain.emergencyCallingEnabled" class="sip-error-item">
											<v-icon x-small color="red">mdi-close-circle</v-icon>
											<span>{{ $t('e911-not-enabled') }}</span>
										</div>
										<div v-if="!org.sipDomain.emergencyCallerSid" class="sip-error-item">
											<v-icon x-small color="red">mdi-close-circle</v-icon>
											<span>{{ $t('emergency-caller-id-not-configured') }}</span>
										</div>
									</div>
								</div>

								<!-- No SIP Domain Warning -->
								<div v-else class="no-sip-domain">
									<v-icon small color="orange">mdi-alert</v-icon>
									<span>{{ $t('no-sip-domains') }}</span>
								</div>

								<!-- Organization Warnings (filtered) -->
								<div v-if="getFilteredWarnings(org).length" class="org-warnings">
									<div
										v-for="(warning, wIndex) in getFilteredWarnings(org)"
										:key="'warning-' + wIndex"
										class="warning-item"
									>
										<v-icon small color="orange">mdi-alert</v-icon>
										<span>{{ warning }}</span>
									</div>
								</div>

								<!-- Phone Numbers by Status -->
								<div class="phone-statuses">
									<!-- Registered (Compliant) -->
									<div v-if="org.phoneNumbers.registered.length" class="status-group registered">
										<div class="status-header">
											<v-icon small color="green">mdi-check-circle</v-icon>
											<span class="status-title">{{ $t('status-registered') }}</span>
											<v-chip x-small color="green" text-color="white">
												{{ org.phoneNumbers.registered.length }}
											</v-chip>
										</div>
										<div class="phones-grid">
											<div v-for="phone in org.phoneNumbers.registered" :key="phone.sid" class="phone-item registered">
												<span>{{ phone.phoneNumber }}</span>
												<v-btn
													icon
													x-small
													color="red"
													:loading="unregisteringPhone === phone.sid"
													@click.stop="confirmUnregister(org, phone)"
												>
													<v-icon x-small>mdi-link-off</v-icon>
												</v-btn>
											</div>
										</div>
									</div>

									<!-- Pending Registration -->
									<div v-if="org.phoneNumbers.pendingRegistration.length" class="status-group pending">
										<div class="status-header">
											<v-icon small color="blue">mdi-clock-outline</v-icon>
											<span class="status-title">{{ $t('status-pending-registration') }}</span>
											<v-chip x-small color="blue" text-color="white">
												{{ org.phoneNumbers.pendingRegistration.length }}
											</v-chip>
										</div>
										<div class="phones-grid">
											<div v-for="phone in org.phoneNumbers.pendingRegistration" :key="phone.sid" class="phone-item pending">
												<span>{{ phone.phoneNumber }}</span>
												<v-btn
													icon
													x-small
													color="red"
													:loading="unregisteringPhone === phone.sid"
													@click.stop="confirmUnregister(org, phone)"
												>
													<v-icon x-small>mdi-link-off</v-icon>
												</v-btn>
											</div>
										</div>
									</div>

									<!-- Unregistered -->
									<div v-if="org.phoneNumbers.unregistered.length" class="status-group unregistered">
										<div class="status-header">
											<v-icon small color="orange">mdi-minus-circle</v-icon>
											<span class="status-title">{{ $t('status-unregistered') }}</span>
											<v-chip x-small color="orange" text-color="white">
												{{ org.phoneNumbers.unregistered.length }}
											</v-chip>
										</div>
										<div class="phones-grid">
											<div v-for="phone in org.phoneNumbers.unregistered" :key="phone.sid" class="phone-item unregistered">
												<span>{{ phone.phoneNumber }}</span>
												<div class="phone-actions">
													<v-btn
														icon
														x-small
														color="green"
														@click.stop="openRegisterDialog(org, phone)"
													>
														<v-icon x-small>mdi-link-plus</v-icon>
													</v-btn>
													<v-btn
														icon
														x-small
														color="red"
														:loading="unregisteringPhone === phone.sid"
														@click.stop="confirmUnregister(org, phone)"
													>
														<v-icon x-small>mdi-link-off</v-icon>
													</v-btn>
												</div>
											</div>
										</div>
									</div>

									<!-- No Address Configured -->
									<div v-if="org.phoneNumbers.noAddressConfigured.length" class="status-group no-address">
										<div class="status-header">
											<v-icon small color="red">mdi-map-marker-off</v-icon>
											<span class="status-title">{{ $t('status-no-address-configured') }}</span>
											<v-chip x-small color="red" text-color="white">
												{{ org.phoneNumbers.noAddressConfigured.length }}
											</v-chip>
										</div>
										<div class="phones-grid">
											<div v-for="phone in org.phoneNumbers.noAddressConfigured" :key="phone.sid" class="phone-item no-address">
												<span>{{ phone.phoneNumber }}</span>
												<v-btn
													icon
													x-small
													color="green"
													@click.stop="openRegisterDialog(org, phone)"
												>
													<v-icon x-small>mdi-link-plus</v-icon>
												</v-btn>
											</div>
										</div>
									</div>

									<!-- Registration Failure -->
									<div v-if="org.phoneNumbers.registrationFailure.length" class="status-group failure">
										<div class="status-header">
											<v-icon small color="red darken-2">mdi-alert-circle</v-icon>
											<span class="status-title">{{ $t('status-registration-failed') }}</span>
											<v-chip x-small color="red darken-2" text-color="white">
												{{ org.phoneNumbers.registrationFailure.length }}
											</v-chip>
										</div>
										<div class="phones-grid">
											<div v-for="phone in org.phoneNumbers.registrationFailure" :key="phone.sid" class="phone-item failure">
												<span>{{ phone.phoneNumber }}</span>
												<div class="phone-actions">
													<v-btn
														icon
														x-small
														color="green"
														@click.stop="openRegisterDialog(org, phone)"
													>
														<v-icon x-small>mdi-link-plus</v-icon>
													</v-btn>
													<span class="action-separator">/</span>
													<v-btn
														icon
														x-small
														color="red"
														:loading="unregisteringPhone === phone.sid"
														@click.stop="confirmUnregister(org, phone)"
													>
														<v-icon x-small>mdi-link-off</v-icon>
													</v-btn>
												</div>
											</div>
										</div>
									</div>

									<!-- Pending Unregistration -->
									<div v-if="org.phoneNumbers.pendingUnregistration.length" class="status-group pending-unreg">
										<div class="status-header">
											<v-icon small color="grey">mdi-clock-alert-outline</v-icon>
											<span class="status-title">{{ $t('status-pending-unregistration') }}</span>
											<v-chip x-small color="grey" text-color="white">
												{{ org.phoneNumbers.pendingUnregistration.length }}
											</v-chip>
										</div>
										<div class="phones-grid">
											<div v-for="phone in org.phoneNumbers.pendingUnregistration" :key="phone.sid" class="phone-item pending-unreg">
												<span>{{ phone.phoneNumber }}</span>
												<v-btn
													icon
													x-small
													color="red"
													:loading="unregisteringPhone === phone.sid"
													@click.stop="confirmUnregister(org, phone)"
												>
													<v-icon x-small>mdi-link-off</v-icon>
												</v-btn>
											</div>
										</div>
									</div>

									<!-- Unregistration Failure -->
									<div v-if="org.phoneNumbers.unregistrationFailure.length" class="status-group unreg-failure">
										<div class="status-header">
											<v-icon small color="deep-orange">mdi-close-circle</v-icon>
											<span class="status-title">{{ $t('status-unregistration-failed') }}</span>
											<v-chip x-small color="deep-orange" text-color="white">
												{{ org.phoneNumbers.unregistrationFailure.length }}
											</v-chip>
										</div>
										<div class="phones-grid">
											<div v-for="phone in org.phoneNumbers.unregistrationFailure" :key="phone.sid" class="phone-item unreg-failure">
												<span>{{ phone.phoneNumber }}</span>
												<v-btn
													icon
													x-small
													color="red"
													:loading="unregisteringPhone === phone.sid"
													@click.stop="confirmUnregister(org, phone)"
												>
													<v-icon x-small>mdi-link-off</v-icon>
												</v-btn>
											</div>
										</div>
									</div>

									<!-- No Phone Numbers -->
									<div v-if="org.totalCount === 0" class="no-phones">
										<v-icon color="grey">mdi-phone-off</v-icon>
										<span>{{ $t('no-phone-numbers') }}</span>
									</div>
								</div>
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-expansion-panels>
				</div>
			</div>

			<!-- Empty State -->
			<div v-else class="empty-state">
				<v-icon large color="grey">mdi-map-marker-question</v-icon>
				<span>{{ $t('no-data') }}</span>
			</div>
		</v-card>

		<!-- Unregister Confirmation Dialog -->
		<v-dialog v-model="unregisterDialog" max-width="480" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="text-h6">
					<v-icon left color="red">mdi-link-off</v-icon>
					{{ $t('remove-emergency-address') }}
					<v-spacer />
					<v-btn icon small @click="unregisterDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text v-if="unregisterTarget">
					{{ $t('confirm-remove-emergency-address') }}
					<strong>{{ unregisterTarget.phone.phoneNumber }}</strong>?
					<br><br>
					<span class="text-caption">{{ $t('organization') }}: {{ unregisterTarget.org.organizationName || unregisterTarget.org.organizationId }}</span>

					<!-- Address details -->
					<div v-if="loadingUnregisterAddress" class="text-center py-3">
						<v-progress-circular indeterminate color="red" size="20" width="2" />
						<span class="ml-2 text-caption">{{ $t('loading-address-details') }}</span>
					</div>
					<v-alert
						v-else-if="unregisterAddressDetails"
						type="info"
						outlined
						dense
						class="mt-3 mb-0"
					>
						<div class="address-info">
							<div class="font-weight-medium">
								{{ unregisterAddressDetails.friendlyName || unregisterAddressDetails.customerName }}
							</div>
							<div class="text-caption">
								{{ unregisterAddressDetails.street }}, {{ unregisterAddressDetails.city }}, {{ unregisterAddressDetails.region }} {{ unregisterAddressDetails.postalCode }}
							</div>
							<div class="text-caption" style="opacity: 0.7">
								SID: {{ unregisterAddressDetails.sid }}
							</div>
						</div>
					</v-alert>
					<v-alert
						v-else-if="unregisterTarget.phone.emergencyAddressSid"
						type="warning"
						outlined
						dense
						class="mt-3 mb-0"
					>
						{{ $t('address-sid') }}: {{ unregisterTarget.phone.emergencyAddressSid }}
						<br>
						<span class="text-caption">{{ $t('could-not-load-address-details') }}</span>
					</v-alert>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="unregisterDialog = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="red darken-1"
						dark
						:loading="unregisteringPhone !== null"
						@click="executeUnregister"
					>
						{{ $t('remove') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Register Address Dialog -->
		<v-dialog v-model="registerDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="text-h6">
					<v-icon left color="green">mdi-link-plus</v-icon>
					{{ $t('set-emergency-address') }}
					<v-spacer />
					<v-btn icon small @click="registerDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text v-if="registerTarget">
					<p>
						{{ $t('select-address-for') }}
						<strong>{{ registerTarget.phone.phoneNumber }}</strong>:
					</p>

					<div v-if="loadingAddresses" class="text-center py-4">
						<v-progress-circular indeterminate color="primary" size="24" />
						<span class="ml-2">{{ $t('loading-addresses') }}</span>
					</div>

					<v-radio-group
						v-else-if="availableAddresses.length"
						v-model="selectedAddressSid"
						class="address-radio-group"
					>
						<v-radio
							v-for="addr in availableAddresses"
							:key="addr.sid"
							:value="addr.sid"
							class="address-radio"
						>
							<template #label>
								<div class="address-label">
									<strong>{{ addr.friendlyName || addr.customerName }}</strong>
									<span class="address-detail">{{ addr.street }}, {{ addr.city }}, {{ addr.region }} {{ addr.postalCode }}</span>
								</div>
							</template>
						</v-radio>
					</v-radio-group>

					<v-alert v-else type="warning" dense outlined>
						{{ $t('no-addresses-found') }}
					</v-alert>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="registerDialog = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="green darken-1"
						dark
						:loading="registeringPhone !== null"
						:disabled="!selectedAddressSid"
						@click="executeRegister"
					>
						{{ $t('set-address') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
export default {
	name: "EmergencyAddressPanel",

	data() {
		return {
			loading: false,
			error: null,
			report: null,
			expandedPanels: [],
			searchText: "",
			// Unregister dialog
			unregisterDialog: false,
			unregisterTarget: null,
			unregisteringPhone: null,
			loadingUnregisterAddress: false,
			unregisterAddressDetails: null,
			// Register dialog
			registerDialog: false,
			registerTarget: null,
			registeringPhone: null,
			loadingAddresses: false,
			availableAddresses: [],
			selectedAddressSid: null
		};
	},

	computed: {
		sortedOrganizations() {
			if (!this.report?.organizations) return [];

			const orgs = [...this.report.organizations];

			// Split into non-compliant and compliant (considering SIP domain too)
			const nonCompliant = orgs.filter(o => !this.isOrgCompliant(o));
			const compliant = orgs.filter(o => this.isOrgCompliant(o));

			// Sort each group alphabetically by organizationId
			nonCompliant.sort((a, b) => a.organizationId.localeCompare(b.organizationId));
			compliant.sort((a, b) => a.organizationId.localeCompare(b.organizationId));

			// Return non-compliant first, then compliant
			return [...nonCompliant, ...compliant];
		},

		filteredOrganizations() {
			if (!this.searchText) {
				return this.sortedOrganizations;
			}

			const searchNormalized = this.normalizeText(this.searchText);

			return this.sortedOrganizations.filter(org => {
				const orgName = this.normalizeText(org.organizationName || "");
				const orgId = this.normalizeText(org.organizationId || "");

				return orgName.includes(searchNormalized) || orgId.includes(searchNormalized);
			});
		},

		compliantOrganizationsCount() {
			if (!this.report?.organizations) return 0;
			return this.report.organizations.filter(o => this.isOrgCompliant(o)).length;
		}
	},

	methods: {
		async fetchReport() {
			this.loading = true;
			this.error = null;

			try {
				const response = await this.axios.get("/emergency-address/emergency/address/report");

				this.report = response.data;
				// Start with all panels collapsed
				this.expandedPanels = [];
			} catch (err) {
				console.error("Error fetching emergency address report:", err);
				this.error = err.response?.data?.message || err.message || this.$t("error-generic");
			} finally {
				this.loading = false;
			}
		},

		getFilteredWarnings(org) {
			if (!org.warnings || !org.warnings.length) return [];
			// Filter out SIP domain related warnings since we show that separately
			return org.warnings.filter(w => {
				const lower = w.toLowerCase();

				return !lower.includes("sip domain") && !lower.includes("sipdomain");
			});
		},

		isSipDomainCompliant(sipDomain) {
			return sipDomain && sipDomain.emergencyCallingEnabled && sipDomain.emergencyCallerSid;
		},

		isOrgCompliant(org) {
			// Check phone number compliance
			if (org.nonCompliantCount > 0) return false;

			// Check SIP domain compliance (if SIP domain exists, it must be properly configured)
			if (org.sipDomain) {
				if (!org.sipDomain.emergencyCallingEnabled || !org.sipDomain.emergencyCallerSid) {
					return false;
				}
			}

			return true;
		},

		async copyToClipboard(value) {
			if (!value) return;
			try {
				await navigator.clipboard.writeText(String(value));
				this.$success(this.$t("copied-to-clipboard"));
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		normalizeText(text) {
			return text
				.toLowerCase()
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "");
		},

		// --- Unregister Emergency Address ---
		async confirmUnregister(org, phone) {
			this.unregisterTarget = { org, phone };
			this.unregisterAddressDetails = null;
			this.unregisterDialog = true;

			if (phone.emergencyAddressSid) {
				this.loadingUnregisterAddress = true;
				try {
					const response = await this.axios.get(
						`/devops/twilio/addresses/${org.accountSid}`
					);
					const match = response.data.find(a => a.sid === phone.emergencyAddressSid);

					if (match) {
						this.unregisterAddressDetails = match;
					}
				} catch (err) {
					console.error("Error fetching address details for unregister:", err);
				} finally {
					this.loadingUnregisterAddress = false;
				}
			}
		},

		async executeUnregister() {
			if (!this.unregisterTarget) return;

			const { org, phone } = this.unregisterTarget;

			this.unregisteringPhone = phone.sid;

			try {
				await this.axios.delete(
					`/devops/twilio/phone-numbers/${org.accountSid}/${phone.sid}/emergency-address`
				);

				this.$success(this.$t("emergency-address-removed", { phoneNumber: phone.phoneNumber }));
				this.unregisterDialog = false;
				this.unregisterTarget = null;
				// Refresh the report
				await this.fetchReport();
			} catch (err) {
				console.error("Error removing emergency address:", err);
				this.$error(err.response?.data?.message || err.message || this.$t("failed-remove-emergency-address"));
			} finally {
				this.unregisteringPhone = null;
			}
		},

		// --- Register Emergency Address ---
		async openRegisterDialog(org, phone) {
			this.registerTarget = { org, phone };
			this.selectedAddressSid = null;
			this.availableAddresses = [];
			this.registerDialog = true;
			this.loadingAddresses = true;

			try {
				const response = await this.axios.get(
					`/devops/twilio/addresses/${org.accountSid}`
				);

				this.availableAddresses = response.data;
			} catch (err) {
				console.error("Error fetching addresses:", err);
				this.availableAddresses = [];
			} finally {
				this.loadingAddresses = false;
			}
		},

		async executeRegister() {
			if (!this.registerTarget || !this.selectedAddressSid) return;

			const { org, phone } = this.registerTarget;

			this.registeringPhone = phone.sid;

			try {
				await this.axios.post(
					`/devops/twilio/phone-numbers/${org.accountSid}/${phone.sid}/emergency-address`,
					{ emergencyAddressSid: this.selectedAddressSid }
				);

				this.$success(this.$t("emergency-address-set", { phoneNumber: phone.phoneNumber }));
				this.registerDialog = false;
				this.registerTarget = null;
				this.selectedAddressSid = null;
				// Refresh the report
				await this.fetchReport();
			} catch (err) {
				console.error("Error setting emergency address:", err);
				this.$error(err.response?.data?.message || err.message || this.$t("failed-set-emergency-address"));
			} finally {
				this.registeringPhone = null;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.emergency-address-panel {
	overflow: hidden;
}

.panel-container {
	overflow: hidden;
}

.initial-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 48px 24px;
	text-align: center;
	min-height: 300px;

	.initial-title {
		font-size: 18px;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.87);
		margin-bottom: 8px;
	}

	.initial-description {
		font-size: 14px;
		color: rgba(0, 0, 0, 0.6);
		max-width: 400px;
		margin-bottom: 24px;
		line-height: 1.5;
	}

	.generate-btn {
		text-transform: none;
		font-weight: 500;
		letter-spacing: 0.25px;
		padding: 0 32px;
	}
}

.loading-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 48px 24px;
	gap: 16px;
	min-height: 300px;

	.loading-text {
		color: rgba(0, 0, 0, 0.6);
		font-size: 14px;
	}
}

.content-container {
	padding: 12px;
	max-height: 500px;
	overflow-y: auto;
}

@media (max-width: 599px) {
	.emergency-address-panel,
	.panel-container {
		overflow: visible;
	}

	.emergency-address-panel {
		display: flex;
		flex-direction: column;
		height: 100%;
	}

	.panel-container {
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	.content-container {
		max-height: none;
		flex: 1;
	}

	.initial-state,
	.loading-container {
		min-height: 0;
	}
}

.queue-status {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 6px 10px;
	border-radius: 6px;
	font-size: 11px;
	font-weight: 500;
	margin-bottom: 10px;

	&.queue-empty {
		background: #e8f5e9;
		color: #2e7d32;
	}

	&.queue-processing {
		background: #fff3e0;
		color: #e65100;
	}
}

.summary-section {
	background: linear-gradient(135deg, #ffebee 0%, #fff 100%);
	border-radius: 8px;
	padding: 12px;
	margin-bottom: 12px;
	border: 1px solid #ffcdd2;

	.summary-stats {
		display: flex;
		gap: 12px;
		flex-wrap: wrap;

		.stat-item {
			display: flex;
			flex-direction: column;
			align-items: center;
			padding: 8px 16px;
			background: white;
			border-radius: 6px;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
			min-width: 70px;
			flex: 1;

			.stat-value {
				font-size: 20px;
				font-weight: 700;
			}

			.stat-label {
				font-size: 10px;
				color: rgba(0, 0, 0, 0.6);
				text-transform: uppercase;
				letter-spacing: 0.5px;
			}

			&.compliant .stat-value {
				color: #2e7d32;
			}

			&.non-compliant .stat-value {
				color: #c62828;
			}

			&.total .stat-value {
				color: #1565c0;
			}
		}
	}
}

.organizations-section {
	.section-header {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;

		.section-title {
			font-size: 13px;
			font-weight: 600;
			color: rgba(0, 0, 0, 0.87);
		}

		.org-count-chip {
			background: #e3f2fd !important;
			color: #1565c0;
			font-size: 11px;
		}
	}

	.search-container {
		margin-bottom: 12px;

		.search-field {
			font-size: 13px;
		}

		.results-count {
			font-size: 11px;
			color: rgba(0, 0, 0, 0.5);
			margin-top: 4px;
			text-align: right;
		}
	}
}

.org-panel {
	border: 1px solid #e0e0e0;
	border-radius: 6px !important;
	margin-bottom: 6px;
	overflow: hidden;

	&.org-inactive {
		opacity: 0.7;
		background: #fafafa;
	}

	&::before {
		box-shadow: none !important;
	}
}

.org-header {
	padding: 8px 12px !important;
	min-height: 48px !important;

	.org-header-content {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		gap: 12px;
	}

	.org-info {
		display: flex;
		flex-direction: column;
		min-width: 0;

		.org-name-row {
			display: flex;
			align-items: center;
		}

		.org-name {
			font-weight: 600;
			font-size: 13px;
			color: rgba(0, 0, 0, 0.87);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		.org-id-row {
			display: flex;
			align-items: center;
			gap: 4px;
		}

		.org-id {
			font-size: 10px;
			color: rgba(0, 0, 0, 0.5);
			font-family: monospace;
		}

		.copy-btn {
			opacity: 0.5;
			transition: opacity 0.2s;

			&:hover {
				opacity: 1;
			}
		}
	}

	.org-status {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;

		.status-chip {
			font-weight: 500;
			font-size: 10px;
		}

		.phone-count {
			font-size: 11px;
			color: rgba(0, 0, 0, 0.6);
			white-space: nowrap;
		}
	}

	// Mobile responsive - stack layout on xs breakpoint
	@media (max-width: 599px) {
		.org-header-content {
			flex-direction: column;
			align-items: flex-start;
			gap: 6px;
		}

		.org-status {
			width: 100%;
			justify-content: flex-start;
		}
	}
}

.org-warnings {
	margin-bottom: 10px;
	display: flex;
	flex-direction: column;
	gap: 4px;

	.warning-item {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 12px;
		background: #fff3e0;
		border-radius: 6px;
		font-size: 12px;
		color: #e65100;

		.v-icon {
			font-size: 16px !important;
		}
	}
}

.sip-domain-card {
	background: #e3f2fd;
	border-radius: 6px;
	padding: 8px 12px;
	margin-bottom: 12px;

	&.sip-domain-error {
		background: #ffebee;
		border: 1px solid #ffcdd2;
	}

	.sip-domain-header {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;

		.domain-name {
			font-weight: 500;
			font-size: 12px;
			flex-grow: 1;
			font-family: monospace;
		}

		.sip-status-chips {
			display: flex;
			gap: 4px;
		}
	}

	.sip-errors {
		margin-top: 8px;
		padding-top: 8px;
		border-top: 1px solid rgba(0, 0, 0, 0.1);
		display: flex;
		flex-direction: column;
		gap: 4px;

		.sip-error-item {
			display: flex;
			align-items: center;
			gap: 4px;
			font-size: 11px;
			color: #c62828;
		}
	}
}

.no-sip-domain {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 12px;
	background: #fff3e0;
	border-radius: 6px;
	margin-bottom: 12px;
	font-size: 12px;
	color: #e65100;
}

.phone-statuses {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.status-group {
	background: #fafafa;
	border-radius: 6px;
	padding: 10px;
	border-left: 3px solid;

	&.registered {
		border-left-color: #4caf50;
		background: #e8f5e9;
	}

	&.pending {
		border-left-color: #2196f3;
		background: #e3f2fd;
	}

	&.unregistered {
		border-left-color: #ff9800;
		background: #fff3e0;
	}

	&.no-address {
		border-left-color: #f44336;
		background: #ffebee;
	}

	&.failure {
		border-left-color: #c62828;
		background: #ffcdd2;
	}

	&.pending-unreg {
		border-left-color: #9e9e9e;
		background: #f5f5f5;
	}

	&.unreg-failure {
		border-left-color: #ff5722;
		background: #fbe9e7;
	}

	.status-header {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-bottom: 8px;

		.status-title {
			font-weight: 600;
			font-size: 11px;
			flex-grow: 1;
		}
	}
}

.phones-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;

	.phone-item {
		display: flex;
		align-items: center;
		gap: 4px;
		font-family: monospace;
		font-size: 11px;
		padding: 4px 8px;
		border-radius: 4px;
		background: white;
		border: 1px solid;

		.phone-actions {
			display: flex;
			gap: 2px;
			align-items: center;

			.action-separator {
				font-size: 11px;
				color: rgba(0, 0, 0, 0.3);
				margin: 0 2px;
			}
		}

		&.registered {
			border-color: #a5d6a7;
			color: #2e7d32;
		}

		&.pending {
			border-color: #90caf9;
			color: #1565c0;
		}

		&.unregistered {
			border-color: #ffcc80;
			color: #e65100;
		}

		&.no-address {
			border-color: #ef9a9a;
			color: #c62828;
		}

		&.failure {
			border-color: #e57373;
			color: #b71c1c;
		}

		&.pending-unreg {
			border-color: #bdbdbd;
			color: #616161;
		}

		&.unreg-failure {
			border-color: #ffab91;
			color: #d84315;
		}
	}
}

.address-radio-group {
	.address-radio {
		margin-bottom: 8px;

		.address-label {
			display: flex;
			flex-direction: column;
			font-size: 13px;
			line-height: 1.4;

			.address-detail {
				font-size: 11px;
				color: rgba(0, 0, 0, 0.6);
			}
		}
	}
}

.no-phones {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 16px;
	color: rgba(0, 0, 0, 0.5);
	font-size: 12px;
}

.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 48px;
	gap: 12px;
	color: rgba(0, 0, 0, 0.5);
}
</style>
